import React, { useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import MobileBottomNav from "./MobileBottomNav";
import NewJobModal from "../NewJobModal";

/**
 * Global host for the mobile bottom navigation bar.
 *
 * Renders once at the app root so the nav appears on EVERY page for
 * authenticated users on mobile viewports (< md breakpoint).
 *
 * Also mounts a single shared <NewJobModal/> here so the "+ Create Job"
 * affordance works from any mobile page (MobileJobs' FAB, MobileHome's
 * Today's Jobs card) via the shared `open-new-job` window event, even on
 * /mobile which sits outside the desktop Layout (and its own NewJobModal).
 *
 * Hides itself on:
 *   - unauthenticated / loading auth states
 *   - public marketing/auth/payment/form routes
 *   - the /platform super-admin surface (has its own layout)
 *
 * Active-tab is inferred from the current pathname + `?tab=` query.
 */

const HIDE_EXACT = new Set([
  "/",
  "/login",
  "/register",
  "/signup",
  "/pricing",
  "/forgot-password",
  "/reset-password",
  "/accept-invite",
  "/auth/callback",
]);

const HIDE_PREFIX = ["/platform", "/pay/", "/accept/", "/f/"];

function computeActiveTab(pathname, searchParams) {
  if (pathname === "/mobile/calendar") return "calendar";
  if (pathname === "/messages") return "messages";
  if (pathname === "/jobs") return "jobs";
  if (pathname === "/dashboard" || pathname === "/mobile") {
    const tab = searchParams.get("tab") || "overview";
    return tab === "overview" ? "overview" : null; // other Home tabs (all-jobs, tasks, history, etc.)
  }
  return null;
}

export default function MobileNavHost() {
  const { user } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [newJobOpen, setNewJobOpen] = useState(false);

  useEffect(() => {
    const openIt = () => setNewJobOpen(true);
    window.addEventListener("open-new-job", openIt);
    return () => window.removeEventListener("open-new-job", openIt);
  }, []);

  if (!user) return null;

  const path = location.pathname;
  if (HIDE_EXACT.has(path)) return null;
  if (HIDE_PREFIX.some((p) => path === p.replace(/\/$/, "") || path.startsWith(p))) return null;

  const activeTab = computeActiveTab(path, searchParams);

  return (
    <div className="md:hidden" data-testid="mobile-nav-host">
      <MobileBottomNav activeTab={activeTab} />
      <NewJobModal
        open={newJobOpen}
        onClose={() => setNewJobOpen(false)}
        onCreated={(job) => window.dispatchEvent(new CustomEvent("job-created", { detail: job }))}
      />
    </div>
  );
}
